import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../utils/axios';
import { AdminSidebar } from './AdminLeaderboardWinners';
import { Ticket, Trash2, CheckCircle, XCircle, Clock, Eye, X, RefreshCw, Download, CalendarDays, CheckSquare, Square, Search } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rewardBadge = (status) => {
  if (status === 'winner') return { label: 'Berhasil', cls: 'bg-green-500/20 text-green-400 border border-green-500/30', icon: CheckCircle };
  if (status === 'not_winner') return { label: 'Gagal', cls: 'bg-red-500/20 text-red-400 border border-red-500/30', icon: XCircle };
  if (status === 'disqualified') return { label: 'Diskualifikasi', cls: 'bg-gray-500/20 text-gray-400 border border-gray-500/30', icon: XCircle };
  return { label: 'Pending', cls: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30', icon: Clock };
};

export const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filterStatus !== 'all') params.reward_status = filterStatus;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await axios.get(`${API_BASE}/admin/tickets`, {
        withCredentials: true,
        params,
      });
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error('Gagal memuat tiket');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, [filterStatus, debouncedSearch]);

  const openDetail = (t) => {
    setSelected(t);
    setNotes('');
  };

  const closeDetail = () => {
    setSelected(null);
    setNotes('');
  };

  const handleValidate = async (ticketId, rewardStatus) => {
    try {
      await axios.put(
        `${API_BASE}/admin/tickets/${ticketId}/validate`,
        { reward_status: rewardStatus, validation_notes: notes || null },
        { withCredentials: true }
      );
      const label = rewardStatus === 'winner' ? 'Berhasil' : rewardStatus === 'not_winner' ? 'Gagal' : rewardStatus;
      toast.success(`Status tiket diubah menjadi: ${label}`);
      fetchTickets();
      closeDetail();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal update status');
    }
  };

  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  const applyDatePreset = (preset) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (preset === 'today') {
      const t = iso(now);
      setExportDateFrom(t); setExportDateTo(t);
    } else if (preset === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      const t = iso(y);
      setExportDateFrom(t); setExportDateTo(t);
    } else if (preset === '7d') {
      const s = new Date(now); s.setDate(s.getDate() - 6);
      setExportDateFrom(iso(s)); setExportDateTo(iso(now));
    } else if (preset === '30d') {
      const s = new Date(now); s.setDate(s.getDate() - 29);
      setExportDateFrom(iso(s)); setExportDateTo(iso(now));
    } else if (preset === 'all') {
      setExportDateFrom(''); setExportDateTo('');
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportDateFrom) params.date_from = `${exportDateFrom}T00:00:00`;
      if (exportDateTo) params.date_to = `${exportDateTo}T23:59:59`;
      const res = await axios.get(`${API_BASE}/admin/predictions/export`, {
        withCredentials: true,
        responseType: 'blob',
        params,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      const label = exportDateFrom && exportDateTo
        ? `${exportDateFrom}_to_${exportDateTo}`
        : exportDateFrom ? `from_${exportDateFrom}` : exportDateTo ? `to_${exportDateTo}` : today;
      a.download = `prediksi_${label}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ekspor Excel berhasil');
      setShowExportModal(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal ekspor Excel');
    } finally {
      setExporting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === tickets.length) setSelectedIds([]);
    else setSelectedIds(tickets.map(t => t.id));
  };
  const clearSelection = () => setSelectedIds([]);

  const handleBulkValidate = async (rewardStatus) => {
    if (selectedIds.length === 0) return;
    const label = rewardStatus === 'winner' ? 'Berhasil' : rewardStatus === 'not_winner' ? 'Gagal' : rewardStatus === 'disqualified' ? 'Diskualifikasi' : rewardStatus;
    if (!window.confirm(`Ubah ${selectedIds.length} tiket menjadi "${label}"?`)) return;
    setBulkAction(rewardStatus);
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/tickets/bulk-validate`,
        { ticket_ids: selectedIds, reward_status: rewardStatus },
        { withCredentials: true }
      );
      toast.success(data.message || `${selectedIds.length} tiket diupdate`);
      clearSelection();
      fetchTickets();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal update massal');
    } finally {
      setBulkAction('');
    }
  };

  const handleDelete = async (ticketId, ticketNumber) => {
    if (!window.confirm(`Yakin ingin menghapus tiket ${ticketNumber}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/tickets/${ticketId}`, { withCredentials: true });
      toast.success('Tiket berhasil dihapus');
      fetchTickets();
      if (selected?.id === ticketId) closeDetail();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal menghapus');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Yakin ingin menghapus ${selectedIds.length} tiket terpilih? Tindakan ini tidak bisa dibatalkan.`)) return;
    setBulkAction('delete');
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/tickets/bulk-delete`,
        { ticket_ids: selectedIds },
        { withCredentials: true }
      );
      toast.success(data.message || `${selectedIds.length} tiket dihapus`);
      clearSelection();
      fetchTickets();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Gagal menghapus massal');
    } finally {
      setBulkAction('');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-tickets-page">
      <AdminSidebar active="/admin/tickets" />

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-4xl font-black text-white mb-2 flex items-center gap-3">
              <Ticket className="text-[#FFD60A] shrink-0" size={28} /> <span className="truncate">Ticket Center</span>
            </h1>
            <p className="text-gray-400 text-sm md:text-base">Kelola tiket prediksi & ubah status Berhasil / Gagal</p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap w-full md:w-auto">
            <button
              onClick={() => setShowExportModal(true)}
              className="btn-secondary flex items-center gap-2 text-sm px-4 py-2"
              data-testid="export-predictions-excel-btn"
              title="Unduh rekap prediksi peserta (bisa pilih rentang tanggal)"
            >
              <Download size={16} /> Ekspor Excel
            </button>
            <button onClick={fetchTickets} className="btn-secondary flex items-center gap-2 text-sm px-4 py-2" data-testid="refresh-tickets-btn">
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="glass-card p-4 mb-4 flex items-center justify-between flex-wrap gap-3 border-2 border-[#FFD60A]/40" data-testid="bulk-action-bar">
            <div className="text-white font-semibold flex items-center gap-2">
              <CheckSquare className="text-[#FFD60A]" size={18} />
              <span data-testid="bulk-selected-count">{selectedIds.length}</span> tiket dipilih
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handleBulkValidate('winner')}
                disabled={bulkAction === 'winner'}
                className="px-4 py-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="bulk-winner-btn"
              >
                <CheckCircle size={14} /> Tandai Berhasil
              </button>
              <button
                onClick={() => handleBulkValidate('not_winner')}
                disabled={bulkAction === 'not_winner'}
                className="px-4 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="bulk-fail-btn"
              >
                <XCircle size={14} /> Tandai Gagal
              </button>
              <button
                onClick={() => handleBulkValidate('disqualified')}
                disabled={bulkAction === 'disqualified'}
                className="px-4 py-2 rounded-full bg-gray-500 hover:bg-gray-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="bulk-disqualified-btn"
              >
                <XCircle size={14} /> Diskualifikasi
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkAction === 'delete'}
                className="px-4 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={14} /> Hapus
              </button>
              <button
                onClick={clearSelection}
                className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold"
                data-testid="bulk-clear-btn"
              >
                Batal
              </button>
            </div>
          </div>
        )}

        <div className="mb-4" data-testid="ticket-search-wrapper">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cari nomor tiket, User ID, atau nama tim..."
              className="!pl-11"
              data-testid="ticket-search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
                data-testid="ticket-search-clear"
                title="Kosongkan pencarian"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {debouncedSearch && (
            <div className="mt-2 text-xs text-gray-400">
              Menampilkan hasil untuk: <span className="text-[#FFD60A] font-mono">"{debouncedSearch}"</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="ticket-filters">
          {[
            { key: 'all', label: 'Semua' },
            { key: 'pending', label: 'Pending' },
            { key: 'winner', label: 'Berhasil' },
            { key: 'not_winner', label: 'Gagal' },
            { key: 'disqualified', label: 'Diskualifikasi' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                filterStatus === f.key
                  ? 'bg-[#D90429] text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="glass-card p-6">
          <div className="mb-4 text-gray-400 text-sm">Total: <span className="text-white font-bold">{tickets.length}</span> tiket</div>
          {loading ? (
            <div className="text-gray-400 text-center py-8">Loading...</div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-16">
              <Ticket className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400">Belum ada tiket</p>
            </div>
          ) : (
            <>
              {/* Desktop / Tablet Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-white/10 text-gray-400 text-left">
                    <tr>
                      <th className="py-3 pr-3 w-10">
                        <button
                          onClick={toggleSelectAll}
                          className="text-[#FFD60A] hover:text-white transition-colors"
                          title={selectedIds.length === tickets.length ? 'Batal pilih semua' : 'Pilih semua'}
                          data-testid="select-all-tickets"
                        >
                          {selectedIds.length === tickets.length && tickets.length > 0
                            ? <CheckSquare size={18} />
                            : <Square size={18} />}
                        </button>
                      </th>
                      <th className="py-3 pr-3">No. Tiket</th>
                      <th className="py-3 pr-3">Website / User ID</th>
                      <th className="py-3 pr-3">Pertandingan</th>
                      <th className="py-3 pr-3">Prediksi</th>
                      <th className="py-3 pr-3">Waktu Submit</th>
                      <th className="py-3 pr-3">Status</th>
                      <th className="py-3">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 text-white">
                    {tickets.map((t) => {
                      const rb = rewardBadge(t.reward_status);
                      return (
                        <tr key={t.id} data-testid={`ticket-row-${t.id}`} className={selectedIds.includes(t.id) ? 'bg-[#FFD60A]/5' : ''}>
                          <td className="py-3 pr-3">
                            <button
                              onClick={() => toggleSelect(t.id)}
                              className="text-[#FFD60A] hover:text-white transition-colors"
                              data-testid={`select-ticket-${t.id}`}
                            >
                              {selectedIds.includes(t.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                            </button>
                          </td>
                          <td className="py-3 pr-3">
                            <div className="font-mono font-bold text-[#FFD60A]">{t.ticket_number}</div>
                            <div className="font-mono text-xs text-gray-500 truncate max-w-[140px]">{t.ticket_code}</div>
                          </td>
                          <td className="py-3 pr-3">
                            <div className="text-sm">{t.website || '-'}</div>
                            <div className="text-xs text-gray-400 font-mono">{t.user_id}</div>
                          </td>
                          <td className="py-3 pr-3">
                            {t.match ? (
                              <>
                                <div className="text-xs text-[#FFD60A]">{t.match.league}</div>
                                <div className="text-sm">{t.match.home_team} vs {t.match.away_team}</div>
                              </>
                            ) : '-'}
                          </td>
                          <td className="py-3 pr-3">
                            <span className="font-bold text-lg">{t.prediction}</span>
                          </td>
                          <td className="py-3 pr-3 text-xs text-gray-400">
                            {t.submitted_at ? new Date(t.submitted_at).toLocaleString('id-ID', { day:'2-digit',month:'short',year:'2-digit',hour:'2-digit',minute:'2-digit', timeZone:'Asia/Jakarta'}) : '-'}
                          </td>
                          <td className="py-3 pr-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${rb.cls}`}>
                              <rb.icon size={12} /> {rb.label}
                            </span>
                          </td>
                          <td className="py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => openDetail(t)}
                                className="p-2 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-400"
                                title="Detail & ubah status"
                                data-testid={`view-ticket-${t.id}`}
                              >
                                <Eye size={14} />
                              </button>
                              <button
                                onClick={() => handleValidate(t.id, 'winner')}
                                className="p-2 rounded bg-green-500/10 hover:bg-green-500/20 text-green-400"
                                title="Tandai Berhasil"
                                data-testid={`winner-ticket-${t.id}`}
                              >
                                <CheckCircle size={14} />
                              </button>
                              <button
                                onClick={() => handleValidate(t.id, 'not_winner')}
                                className="p-2 rounded bg-orange-500/10 hover:bg-orange-500/20 text-orange-400"
                                title="Tandai Gagal"
                                data-testid={`fail-ticket-${t.id}`}
                              >
                                <XCircle size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(t.id, t.ticket_number)}
                                className="p-2 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400"
                                title="Hapus tiket"
                                data-testid={`delete-ticket-${t.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List */}
              <div className="lg:hidden space-y-3" data-testid="ticket-card-list">
                <div className="flex items-center justify-between px-1 pb-2">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-2 text-sm text-white/80 hover:text-white"
                    data-testid="select-all-tickets-mobile"
                  >
                    {selectedIds.length === tickets.length && tickets.length > 0
                      ? <CheckSquare className="text-[#FFD60A]" size={18} />
                      : <Square className="text-[#FFD60A]" size={18} />}
                    {selectedIds.length === tickets.length ? 'Batal Semua' : 'Pilih Semua'}
                  </button>
                  <span className="text-xs text-gray-500">{tickets.length} tiket</span>
                </div>
                {tickets.map((t) => {
                  const rb = rewardBadge(t.reward_status);
                  const isSelected = selectedIds.includes(t.id);
                  return (
                    <div
                      key={t.id}
                      data-testid={`ticket-card-${t.id}`}
                      className={`rounded-xl border p-4 space-y-3 transition-colors ${
                        isSelected
                          ? 'bg-[#FFD60A]/10 border-[#FFD60A]/40'
                          : 'bg-white/[0.04] border-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <button
                          onClick={() => toggleSelect(t.id)}
                          className="text-[#FFD60A] hover:text-white transition-colors mt-0.5 shrink-0"
                          data-testid={`select-ticket-mobile-${t.id}`}
                        >
                          {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono font-black text-[#FFD60A] text-base truncate">{t.ticket_number}</div>
                          <div className="font-mono text-[11px] text-gray-500 truncate">{t.ticket_code}</div>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold shrink-0 ${rb.cls}`}>
                          <rb.icon size={11} /> {rb.label}
                        </span>
                      </div>

                      {t.match && (
                        <div className="border-t border-white/5 pt-3">
                          <div className="text-[10px] text-[#FFD60A] uppercase tracking-wide mb-0.5">{t.match.league}</div>
                          <div className="text-sm text-white leading-snug">
                            {t.match.home_team} <span className="text-gray-500">vs</span> {t.match.away_team}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3 border-t border-white/5 pt-3">
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase mb-0.5">Website</div>
                          <div className="text-xs text-white truncate">{t.website || '-'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase mb-0.5">User ID</div>
                          <div className="text-xs text-white font-mono truncate">{t.user_id || '-'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase mb-0.5">Prediksi</div>
                          <div className="text-lg text-[#FFD60A] font-black tabular-nums">{t.prediction}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase mb-0.5">Submit</div>
                          <div className="text-[11px] text-gray-300">
                            {t.submitted_at ? new Date(t.submitted_at).toLocaleString('id-ID', { day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit', timeZone:'Asia/Jakarta'}) : '-'}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-2 border-t border-white/5 pt-3">
                        <button
                          onClick={() => openDetail(t)}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-[10px] font-semibold"
                          data-testid={`view-ticket-mobile-${t.id}`}
                        >
                          <Eye size={16} /> Detail
                        </button>
                        <button
                          onClick={() => handleValidate(t.id, 'winner')}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 text-[10px] font-semibold"
                          data-testid={`winner-ticket-mobile-${t.id}`}
                        >
                          <CheckCircle size={16} /> Berhasil
                        </button>
                        <button
                          onClick={() => handleValidate(t.id, 'not_winner')}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 text-[10px] font-semibold"
                          data-testid={`fail-ticket-mobile-${t.id}`}
                        >
                          <XCircle size={16} /> Gagal
                        </button>
                        <button
                          onClick={() => handleDelete(t.id, t.ticket_number)}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[10px] font-semibold"
                          data-testid={`delete-ticket-mobile-${t.id}`}
                        >
                          <Trash2 size={16} /> Hapus
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {showExportModal && (
          <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
            <div className="modal-content p-6 md:p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="export-modal">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl md:text-2xl font-black text-white flex items-center gap-2">
                  <CalendarDays className="text-[#FFD60A]" size={22} /> Ekspor Rekap Prediksi
                </h2>
                <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-white"><X size={22} /></button>
              </div>

              <p className="text-gray-400 text-sm mb-4">Pilih rentang tanggal (opsional) lalu unduh file Excel. Kosongkan untuk ekspor <b>semua</b> prediksi.</p>

              <div className="flex flex-wrap gap-2 mb-4" data-testid="export-presets">
                {[
                  { key: 'today', label: 'Hari Ini' },
                  { key: 'yesterday', label: 'Kemarin' },
                  { key: '7d', label: '7 Hari' },
                  { key: '30d', label: '30 Hari' },
                  { key: 'all', label: 'Semua' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => applyDatePreset(p.key)}
                    className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white text-xs font-semibold border border-white/10"
                    data-testid={`preset-${p.key}`}
                  >{p.label}</button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <div>
                  <label className="block text-white text-xs font-semibold mb-1">Dari Tanggal</label>
                  <input type="date" value={exportDateFrom} onChange={(e) => setExportDateFrom(e.target.value)} data-testid="export-date-from" />
                </div>
                <div>
                  <label className="block text-white text-xs font-semibold mb-1">Sampai Tanggal</label>
                  <input type="date" value={exportDateTo} onChange={(e) => setExportDateTo(e.target.value)} data-testid="export-date-to" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="btn-secondary w-full sm:w-auto"
                >Batal</button>
                <button
                  onClick={handleExportExcel}
                  disabled={exporting}
                  className="btn-primary w-full sm:flex-1 flex items-center justify-center gap-2"
                  data-testid="confirm-export-btn"
                >
                  <Download size={16} /> {exporting ? 'Menyiapkan...' : 'Unduh Excel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {selected && (
          <div className="modal-overlay" onClick={closeDetail}>
            <div className="modal-content p-8" onClick={(e) => e.stopPropagation()} data-testid="ticket-detail-modal">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-white">Detail Tiket</h2>
                <button onClick={closeDetail} className="text-gray-400 hover:text-white"><X size={24} /></button>
              </div>

              <div className="glass-card p-4 mb-4 bg-white/5">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-gray-400 text-xs mb-1">Nomor Tiket</div><div className="text-[#FFD60A] font-mono font-bold">{selected.ticket_number}</div></div>
                  <div><div className="text-gray-400 text-xs mb-1">Status</div><div>{rewardBadge(selected.reward_status).label}</div></div>
                  <div className="col-span-2"><div className="text-gray-400 text-xs mb-1">Kode Tiket</div><div className="text-white font-mono break-all">{selected.ticket_code}</div></div>
                  <div><div className="text-gray-400 text-xs mb-1">Website</div><div className="text-white">{selected.website || '-'}</div></div>
                  <div><div className="text-gray-400 text-xs mb-1">User ID</div><div className="text-white font-mono">{selected.user_id}</div></div>
                  {selected.match && (
                    <div className="col-span-2">
                      <div className="text-gray-400 text-xs mb-1">Pertandingan</div>
                      <div className="text-white">{selected.match.league} · {selected.match.home_team} vs {selected.match.away_team}</div>
                    </div>
                  )}
                  <div className="col-span-2"><div className="text-gray-400 text-xs mb-1">Prediksi</div><div className="text-[#FFD60A] font-black text-xl">{selected.prediction}</div></div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-white text-sm font-semibold mb-2">Catatan Validasi (opsional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Contoh: prediksi tepat 2-1"
                  data-testid="validation-notes-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <button
                  onClick={() => handleValidate(selected.id, 'winner')}
                  className="px-4 py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                  data-testid="modal-winner-btn"
                >
                  <CheckCircle size={18} /> Berhasil
                </button>
                <button
                  onClick={() => handleValidate(selected.id, 'not_winner')}
                  className="px-4 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                  data-testid="modal-fail-btn"
                >
                  <XCircle size={18} /> Gagal
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleValidate(selected.id, 'pending')}
                  className="px-4 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold flex items-center justify-center gap-2"
                  data-testid="modal-pending-btn"
                >
                  <Clock size={18} /> Reset ke Pending
                </button>
                <button
                  onClick={() => handleDelete(selected.id, selected.ticket_number)}
                  className="px-4 py-3 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold flex items-center justify-center gap-2"
                  data-testid="modal-delete-btn"
                >
                  <Trash2 size={18} /> Hapus Tiket
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
